import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
  Timestamp,
} from 'firebase/firestore';

export default function UpgradeTier() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const tierToPriceId = {
    Basic: '',
    Gold: 'price_1RfqLdPFcHBgFhdRHcRFEzBa',
    Pro: 'price_1RfqM5PFcHBgFhdRDOXG0WvT',
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const snapshot = await getDocs(collection(db, 'users'));
      setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchUsers();
  }, []);

  const handleTierChange = async (userId, newTier) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const confirmChange = window.confirm(
      `Change ${user.name || user.email}'s tier to "${newTier}"?`
    );
    if (!confirmChange) return;

    const priceId = tierToPriceId[newTier];
    if (newTier !== 'Basic' && !priceId) {
      alert(`Missing Stripe price ID for ${newTier}.`);
      return;
    }

    const userRef = doc(db, 'users', userId);

    const updates = {
      subscriptionTier: newTier,
      subscriptionPriceId: priceId || '',
      subscriptionUpdatedAt: serverTimestamp(),
      tierUpdatedAt: serverTimestamp(),
    };

    if (newTier === 'Basic') {
      updates.isManuallyBilled = false;
      updates.nextBillingDate = null;
    } else {
      updates.isManuallyBilled = true;
      updates.nextBillingDate = Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
    }

    await updateDoc(userRef, updates);

    await addDoc(collection(db, 'admin_logs'), {
      type: 'tier_upgrade',
      action: `Admin Tier Upgrade`,
      userEmail: user.email || '',
      targetUserId: userId,
      adminId: JSON.parse(localStorage.getItem('user'))?.email || 'admin@example.com',
      timestamp: serverTimestamp(),
      details: updates,
    });

    setUsers(users.map(u => u.id === userId ? { ...u, ...updates } : u));
  };

  const filteredUsers = users.filter(user =>
    (user.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.title}>🚀 Upgrade User Tier</h2>

      <input
        type="text"
        placeholder="Search by name or email"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={styles.search}
      />

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Current Tier</th>
            <th style={styles.th}>Next Billing</th>
            <th style={styles.th}>Trial Start</th>
            <th style={styles.th}>Trial Expired</th>
            <th style={styles.th}>Upgrade</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map(user => (
            <tr key={user.id} style={styles.row}>
              <td style={styles.td}>{user.name || '—'}</td>
              <td style={styles.td}>{user.email || '—'}</td>
              <td style={styles.td}>{user.subscriptionTier || 'Basic'}</td>
              <td style={styles.td}>
                {user.nextBillingDate?.seconds
                  ? new Date(user.nextBillingDate.seconds * 1000).toLocaleDateString()
                  : '—'}
              </td>
              <td style={styles.td}>
                {user.trialStartedAt?.seconds
                  ? new Date(user.trialStartedAt.seconds * 1000).toLocaleString()
                  : '—'}
              </td>
              <td style={styles.td}>{user.trialExpired ? '✅' : '❌'}</td>
              <td style={styles.td}>
                <select
                  value={user.subscriptionTier || 'Basic'}
                  onChange={(e) => handleTierChange(user.id, e.target.value)}
                  style={styles.select}
                >
                  <option value="Basic">Basic</option>
                  <option value="Gold">Gold</option>
                  <option value="Pro">Pro</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '1rem',
  },
  search: {
    marginBottom: '1.5rem',
    padding: '0.5rem',
    fontSize: '0.95rem',
    width: '100%',
    maxWidth: '320px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    backgroundColor: '#e0f2f1',
    fontWeight: '600',
    color: '#000',
    fontSize: '0.875rem',
  },
  td: {
    padding: '12px',
    fontSize: '0.875rem',
    color: '#333',
  },
  row: {
    borderBottom: '1px solid #eee',
  },
  select: {
    padding: '0.4rem 0.6rem',
    fontSize: '0.85rem',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
};
